import { unitExpiryForPersistence } from './inventoryItemForm';

/**
 * Aircraft ownership label for a store inventory row (registration or GEN).
 */
export function getInventoryAircraftLabel(option) {
  if (!option || typeof option !== 'object') return '';
  const scope = String(option.aircraftScope || 'GEN').toUpperCase();
  if (scope === 'AIRCRAFT') {
    return String(
      option.aircraftId?.registration
      || option.aircraftRegistration
      || ''
    ).trim();
  }
  const reg = String(option.aircraftRegistration || '').trim().toUpperCase();
  return reg && reg !== 'GEN' ? reg : 'GEN';
}

/**
 * Label for Part Master autocomplete: part number, optional part name in parentheses, then description.
 */
export function formatPartMasterOptionLabel(option) {
  if (!option || typeof option !== 'object') return '';
  const pn = option.partNumber != null ? String(option.partNumber) : '';
  const rawName = option.partName && String(option.partName).trim();
  const name = rawName && rawName !== 'Name Not Found' ? rawName : '';
  const desc = option.description != null ? String(option.description) : '';
  if (name) {
    return desc ? `${pn} (${name}) — ${desc}` : `${pn} (${name})`;
  }
  return desc ? `${pn} — ${desc}` : pn;
}

const PICKER_MAX_RESULTS = 75;

/**
 * Filter store inventory rows by search text (part number, name, description).
 * @param {Array} options - inventory rows
 * @param {string} searchText - current search string (use controlled partSearch, not MUI filter state)
 * @param {{ requireSearchTerm?: boolean, partNumberPriority?: boolean, maxResults?: number }} config
 */
export function filterStoreInventoryOptions(options, searchText, config = {}) {
  const {
    requireSearchTerm = false,
    partNumberPriority = false,
    maxResults = PICKER_MAX_RESULTS
  } = config;
  const list = Array.isArray(options) ? options : [];
  const inputValue = String(
    searchText != null && typeof searchText === 'object' && 'inputValue' in searchText
      ? searchText.inputValue
      : searchText
  ).trim().toLowerCase();

  if (!inputValue) {
    return requireSearchTerm ? [] : list;
  }

  if (partNumberPriority) {
    const prefixMatches = [];
    const partNumberContains = [];
    const otherMatches = [];
    for (const option of list) {
      const partNumber = String(option?.partNumber || '').toLowerCase();
      const referencePartNumber = String(option?.referencePartNumber || '').toLowerCase();
      if (!partNumber && !referencePartNumber) continue;
      if (partNumber.startsWith(inputValue) || referencePartNumber.startsWith(inputValue)) {
        prefixMatches.push(option);
        continue;
      }
      if (partNumber.includes(inputValue) || referencePartNumber.includes(inputValue)) {
        partNumberContains.push(option);
        continue;
      }
      const partName = String(option?.partName || '').toLowerCase();
      const description = String(option?.description || '').toLowerCase();
      const aircraft = getInventoryAircraftLabel(option).toLowerCase();
      if (
        partName.includes(inputValue)
        || description.includes(inputValue)
        || aircraft.includes(inputValue)
      ) {
        otherMatches.push(option);
      }
    }
    return [...prefixMatches, ...partNumberContains, ...otherMatches].slice(0, maxResults);
  }

  const matched = list.filter((option) => {
    const partNumber = String(option?.partNumber || '').toLowerCase();
    const referencePartNumber = String(option?.referencePartNumber || '').toLowerCase();
    const partName = String(option?.partName || '').toLowerCase();
    const description = String(option?.description || '').toLowerCase();
    const aircraft = getInventoryAircraftLabel(option).toLowerCase();
    const label = formatPartMasterOptionLabel(option).toLowerCase();
    return (
      partNumber.includes(inputValue) ||
      referencePartNumber.includes(inputValue) ||
      partName.includes(inputValue) ||
      description.includes(inputValue) ||
      aircraft.includes(inputValue) ||
      label.includes(inputValue)
    );
  });
  return matched.slice(0, maxResults);
}

/** Options for part-number Autocomplete — pre-filtered from controlled search text. */
export function inventoryPickerOptions(inventory, searchText) {
  return filterStoreInventoryOptions(inventory, searchText, {
    requireSearchTerm: true,
    partNumberPriority: true
  });
}

export function inventoryOptionLabel(option) {
  if (!option || typeof option !== 'object') return '';
  const base = formatPartMasterOptionLabel(option);
  const aircraft = getInventoryAircraftLabel(option);
  const aircraftSuffix = aircraft ? ` [${aircraft}]` : '';
  const qty = option.quantity != null ? ` (Qty: ${option.quantity})` : '';
  return `${base}${aircraftSuffix}${qty}`;
}

/**
 * Prefer aircraft-scoped stock for a part number, then GEN, then any matching row.
 * @param {Array} inventoryList
 * @param {string} partNumber
 * @param {string} [preferredAircraftId]
 */
export function findPreferredInventoryForPart(inventoryList, partNumber, preferredAircraftId) {
  const pn = String(partNumber || '').trim().toUpperCase();
  if (!pn) return null;
  const candidates = (Array.isArray(inventoryList) ? inventoryList : []).filter(
    (inv) => String(inv?.partNumber || '').trim().toUpperCase() === pn
  );
  if (candidates.length === 0) return null;

  const preferredId = preferredAircraftId != null ? String(preferredAircraftId) : '';
  if (preferredId) {
    const aircraftMatch = candidates.find((inv) => {
      const scope = String(inv?.aircraftScope || 'GEN').toUpperCase();
      if (scope !== 'AIRCRAFT') return false;
      const id = inv?.aircraftId?._id || inv?.aircraftId;
      return id != null && String(id) === preferredId;
    });
    if (aircraftMatch) return aircraftMatch;
  }

  const genMatch = candidates.find(
    (inv) => String(inv?.aircraftScope || 'GEN').toUpperCase() === 'GEN'
  );
  if (genMatch) return genMatch;
  return candidates[0];
}

function addInventorySerialValue(merged, value) {
  const raw = String(value || '').trim();
  if (!raw || isPlaceholderSerialNumber(raw)) return;
  if (raw.includes(',')) {
    raw.split(',').forEach((part) => {
      const s = part.trim();
      if (s && !isPlaceholderSerialNumber(s)) merged.add(s);
    });
    return;
  }
  merged.add(raw);
}

/** Blank / N/A / similar — not a real identifying serial for tag/issuance selection. */
export function isPlaceholderSerialNumber(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return true;
  const u = raw.toUpperCase().replace(/[./\s_\-—–]+/g, '');
  if (!u) return true;
  return u === 'NA' || u === 'NONE' || u === 'NIL';
}

/** All unique serial numbers on an inventory row (array, unit details, and legacy single field). */
export function collectInventorySerialNumbers(inventoryItem) {
  if (!inventoryItem) return [];
  const merged = new Set();
  (Array.isArray(inventoryItem.serialNumbers) ? inventoryItem.serialNumbers : []).forEach((serial) => {
    addInventorySerialValue(merged, serial);
  });
  (Array.isArray(inventoryItem.unitDetails) ? inventoryItem.unitDetails : []).forEach((unit) => {
    addInventorySerialValue(merged, unit?.serialNumber);
  });
  addInventorySerialValue(merged, inventoryItem.serialNumber);
  return Array.from(merged);
}

export function formatInventorySerialNumbersDisplay(inventoryItem) {
  const serials = collectInventorySerialNumbers(inventoryItem);
  return serials.length > 0 ? serials.join(', ') : '';
}

/** Expiry/calibration date from unit details (preferred) or item-level shelf life. */
export function getInventoryDisplayExpiryDate(inventoryItem) {
  if (!inventoryItem) return null;
  const fromUnits = (Array.isArray(inventoryItem.unitDetails) ? inventoryItem.unitDetails : [])
    .map((unit) => unit?.expiryDate)
    .filter(Boolean);
  if (fromUnits.length > 0) {
    // Multiple distinct expiries: show earliest; unit table lists each batch separately.
    return new Date(Math.min(...fromUnits.map((d) => new Date(d).getTime()))).toISOString();
  }
  if (inventoryItem.expiryDate) return inventoryItem.expiryDate;
  return null;
}

/** Batch number from unit-detail rows (preferred) or item-level batch. */
export function getInventoryDisplayBatchNumber(inventoryItem) {
  if (!inventoryItem) return '';
  const batches = (Array.isArray(inventoryItem.unitDetails) ? inventoryItem.unitDetails : [])
    .map((unit) => String(unit?.batchNumber || '').trim())
    .filter((batch) => batch && !isPlaceholderSerialNumber(batch));
  if (batches.length > 0) {
    const unique = [...new Set(batches)];
    return unique.join(', ');
  }
  const lineBatch = String(inventoryItem.batchNumber || '').trim();
  return lineBatch && !isPlaceholderSerialNumber(lineBatch) ? lineBatch : '';
}

export function inventoryUnitDetailsRows(inventoryItem) {
  if (!inventoryItem) return [];
  const rows = Array.isArray(inventoryItem.unitDetails) ? inventoryItem.unitDetails : [];
  return rows.filter(
    (unit) => String(unit?.serialNumber || '').trim() || String(unit?.batchNumber || '').trim()
  );
}

export function inventorySerialOptions(inventoryItem) {
  return collectInventorySerialNumbers(inventoryItem);
}

/** All unique batch numbers on an inventory row (unit details + legacy single field). */
export function collectInventoryBatchNumbers(inventoryItem) {
  if (!inventoryItem) return [];
  const merged = new Set();
  const direct = String(inventoryItem.batchNumber || '').trim();
  if (direct) merged.add(direct);
  (Array.isArray(inventoryItem.unitDetails) ? inventoryItem.unitDetails : []).forEach((unit) => {
    const batch = String(unit?.batchNumber || '').trim();
    if (batch) merged.add(batch);
  });
  return Array.from(merged);
}

export function inventoryBatchOptions(inventoryItem) {
  return collectInventoryBatchNumbers(inventoryItem);
}

/** Match a store inventory unit row by serial number. */
export function findInventoryUnitBySerial(inventoryItem, serialNumber) {
  const target = String(serialNumber || '').trim();
  if (!target || !inventoryItem) return null;
  const rows = Array.isArray(inventoryItem.unitDetails) ? inventoryItem.unitDetails : [];
  return rows.find((unit) => String(unit?.serialNumber || '').trim() === target) || null;
}

/** Build issuance unit rows from selected serials using inventory unit details. */
export function buildIssuanceUnitDetailsFromInventory(inventoryItem, serialNumbers) {
  const serials = (Array.isArray(serialNumbers) ? serialNumbers : [])
    .map((serial) => String(serial || '').trim())
    .filter(Boolean);
  return serials.map((serial) => {
    const matched = findInventoryUnitBySerial(inventoryItem, serial);
    const expiryDate = unitExpiryForPersistence(matched?.expiryDate ?? inventoryItem?.expiryDate);
    return {
      condition: matched?.condition || inventoryItem?.condition || 'SERVICEABLE',
      batchNumber: matched?.batchNumber || inventoryItem?.batchNumber || '',
      serialNumber: serial,
      ...(expiryDate ? { expiryDate } : {})
    };
  });
}

/** Rows to display for issuance when serial(s) are selected (unit details or built from inventory). */
export function issuanceDisplayUnitDetails(inventoryItem, line = {}) {
  const serials = (Array.isArray(line.serialNumbers) ? line.serialNumbers : [])
    .map((serial) => String(serial || '').trim())
    .filter(Boolean);
  if (serials.length === 0) return [];
  const existing = Array.isArray(line.unitDetails) ? line.unitDetails : [];
  if (existing.length > 0) {
    return serials.map((serial) => {
      const row = existing.find((u) => String(u?.serialNumber || '').trim() === serial)
        || findInventoryUnitBySerial(inventoryItem, serial);
      return {
        serialNumber: serial,
        condition: row?.condition || inventoryItem?.condition || 'SERVICEABLE',
        batchNumber: row?.batchNumber || inventoryItem?.batchNumber || '',
        expiryDate: row?.expiryDate || inventoryItem?.expiryDate || null
      };
    });
  }
  return buildIssuanceUnitDetailsFromInventory(inventoryItem, serials);
}
